// Seikkailukortit: korttien valinta, etenemispalkki kartalla ja valmistumisilmoitus.
//
// Kortit laatii palvelin (backend/app/adventures.py) pelaajan ja kavereiden keräämättömistä
// ruuduista ja kohteista. Täällä ne vain näytetään. Kortit eivät riipu lapsen iästä: aikuinen
// valitsee pituuden, ja jokainen kortti on silmukka, joka päättyy lähtöpisteeseen.

import { useEffect, useRef, useState } from "preact/hooks";

import { distanceText, lengthText, nextStop, routeNote, stopsText } from "../lib/adventure.js";
import { currentPosition } from "../lib/location.js";
import { CompanionPicker } from "./PlayerViews.jsx";
import { Sheet } from "./Sheet.jsx";

const LENGTH_LABEL = { short: "Lyhyt", medium: "Keskipitkä", long: "Pitkä" };

/** Kortin kuva pituuden mukaan: verso, puu, vuori. Viivapiirros perii tekstin värin. */
export function Emblem({ kind }) {
  const paths = {
    sprout: <><path d="M12 21v-9" /><path d="M12 12c0-4 3-6 7-6 0 4-3 6-7 6Z" /><path d="M12 15c0-3-2-5-6-5 0 3 2 5 6 5Z" /></>,
    tree: <><path d="M12 21v-5" /><path d="M12 3 6 11h3l-4 6h14l-4-6h3L12 3Z" /></>,
    mountain: <><path d="m2 20 7-13 4 7 2-3 7 9H2Z" /><path d="m7.5 10.5 1.5 1.5 1.5-1.5" /></>,
  };
  return (
    <svg class="adv-emblem" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      {paths[kind] ?? paths.sprout}
    </svg>
  );
}

/** Sijainti korttien hakua varten: tuore selainhaku, simulaattorissa simuloitu sijainti. */
async function locate(app) {
  if (app.simulator && app.me) return currentPosition(app.me);
  try {
    return await currentPosition(null);
  } catch (e) {
    if (app.me) return currentPosition(app.me);
    throw e;
  }
}

function AdventureCard({ card, onPick }) {
  return (
    <article class="adv-card" data-length={card.length} aria-label={`${LENGTH_LABEL[card.length]} seikkailu: ${card.title}`}>
      <Emblem kind={card.emblem} />
      <div class="adv-main">
        <div class="adv-kicker">{LENGTH_LABEL[card.length]}</div>
        <h3>{card.title}</h3>
        <p class="s">{card.story}</p>
        <ul class="adv-facts">
          <li>{stopsText(card.stops.length)}</li>
          <li>{lengthText(card)}</li>
          <li>{card.new_tiles === 1 ? "1 uusi ruutu" : `${card.new_tiles} uutta ruutua`}</li>
          {card.points_estimate > 0 && <li>noin {card.points_estimate} pistettä</li>}
        </ul>
        <button class="btn btn-rasti" onClick={() => onPick(card)}>Valitse</button>
      </div>
    </article>
  );
}

/**
 * Korttien valinta. Ehdotukset haetaan nykyisestä sijainnista; "Uusi ehdotus" arpoo uudet
 * samasta paikasta, ja kaverien valinta muuttaa sen, mikä lasketaan uudeksi ruuduksi.
 */
export function AdventureSheet({ app, onClose, onPick }) {
  const trip = app.rec.active;
  // Retken aikana kaverit on jo valittu: heitä ei voi vaihtaa kesken retken.
  const [mates, setMates] = useState(trip ? trip.companions ?? [] : app.companions);
  const [seed, setSeed] = useState(0);
  const [tries, setTries] = useState(0);
  const [state, setState] = useState({ loading: true, cards: null, error: null });
  const position = useRef(null);
  const request = useRef(0);

  const matesKey = mates.join(",");
  useEffect(() => {
    const mine = ++request.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        position.current ??= await locate(app);
        const { lat, lon } = position.current;
        const found = await app.api.adventures({ lat, lon, seed, companions: mates });
        if (mine === request.current) setState({ loading: false, cards: found.cards, error: null, pending: !!found.terrain_pending });
      } catch (e) {
        if (mine === request.current) setState({ loading: false, cards: null, error: e.message });
      }
    })();
  }, [matesKey, seed, tries]);

  // Uusi yritys: sijainti haetaan uudelleen (esimerkiksi kun lupa on annettu vasta nyt).
  const retry = () => {
    position.current = null;
    setTries((t) => t + 1);
  };

  return (
    <Sheet title="Seikkailukortit" onClose={onClose}>
      <p class="hint">
        Kortti on silmukka, joka alkaa ja päättyy tähän. Valitse pituus, kierrä rastit missä järjestyksessä haluat ja
        leimaa ne kulkemalla paikalle.
      </p>
      {!trip && <CompanionPicker app={app} value={mates} onChange={setMates} />}
      {trip && trip.companions?.length > 0 && <p class="s">Mukana ovat retken kaverit.</p>}

      {state.loading && <p class="s adv-status" role="status">Etsitään sopivia reittejä…</p>}
      {state.error && (
        <div class="notice warn" role="alert">
          {state.error}{" "}
          <button class="btn btn-quiet" onClick={retry}>Yritä uudelleen</button>
        </div>
      )}
      {state.cards && state.cards.length === 0 && (
        <p class="notice" role="status">
          Tältä paikalta ei löytynyt ehdotuksia. Kaikki lähiruudut on joko kerätty tai merkitty mahdottomiksi.
          Siirry toiseen paikkaan ja kokeile uudelleen.
        </p>
      )}
      {state.cards && !state.loading && state.cards.map((card) => <AdventureCard key={card.id} card={card} onPick={(c) => onPick(c, mates)} />)}

      {state.cards?.length > 0 && (
        <>
          <p class="s adv-note">{routeNote(state.cards, state.pending)}</p>
          <div class="sheet-actions">
            <button class="btn btn-block" disabled={state.loading} onClick={() => setSeed(seed + 1)}>Uusi ehdotus</button>
          </div>
        </>
      )}
    </Sheet>
  );
}

/**
 * Valitun seikkailun palkki kartan alla: eteneminen, seuraava rasti ja "Lähde".
 * Leimaus tapahtuu retken aikana, koska silloin sijainti on tiedossa ja ruudut kertyvät.
 */
export function AdventureBar({ app, onStart }) {
  const { adventure, me, rec } = app;
  if (!adventure) return null;
  const { card, reached } = adventure;
  const total = card.stops.length;
  const next = nextStop(card, reached, me ? { lat: me.latitude, lon: me.longitude } : null);

  return (
    <section class="adv-bar" aria-label="Valittu seikkailu">
      <div class="adv-bar-head">
        <Emblem kind={card.emblem} />
        <div class="adv-bar-title">
          <b>{card.title}</b>
          <span class="num" aria-label={`${reached.length} rastia ${total}:sta leimattu`}>{reached.length}/{total} rastia</span>
        </div>
        <button class="btn btn-quiet" onClick={app.cancelAdventure} aria-label="Peru seikkailu">Peru</button>
      </div>
      {/* Retken aikana palkki pidetään matalana, jotta kartta jää näkyviin. */}
      {!rec.active && (
        <ol class="adv-dots" aria-hidden="true">
          {card.stops.map((_, i) => <li key={i} class={reached.includes(i) ? "done" : ""}>{reached.includes(i) ? "✓" : i + 1}</li>)}
        </ol>
      )}
      <div class="adv-bar-foot">
        {next ? (
          <span class="s">
            Seuraava: {next.stop.kind === "poi" ? next.stop.label : `rasti ${next.index + 1}`}
            {next.distance != null && <>, {distanceText(next.distance)}</>}
          </span>
        ) : (
          <span class="s">Kaikki rastit leimattu.</span>
        )}
        {!rec.active && <button class="btn btn-rasti" onClick={onStart}>Lähde</button>}
      </div>
      {!rec.active && <p class="s adv-hint">Rastit leimautuvat retken aikana. Aloita retki, niin ruudut kertyvät samalla.</p>}
    </section>
  );
}

/** Valmistumisilmoitus: seikkailun nimi, kaikkien saamat uudet merkit ja jatko. */
export function AdventureDoneSheet({ done, onClose }) {
  return (
    <Sheet title="Seikkailu suoritettu!" onClose={onClose}>
      <div class="adv-done">
        <Emblem kind={done.card.emblem} />
        <p>
          <b>{done.card.title}</b>: kaikki {stopsText(done.card.stops.length)} leimattu.
        </p>
      </div>
      {done.badges.length > 0 && (
        <ul class="adv-badges" aria-label="Uudet merkit">
          {done.badges.map((b) => <li key={`${b.name}:${b.badge}`}>{b.name ? `${b.name}: ` : ""}<b>{b.badge}</b></li>)}
        </ul>
      )}
      <div class="sheet-actions">
        <button class="btn btn-rasti btn-block" onClick={onClose}>Jatka</button>
      </div>
    </Sheet>
  );
}
